import type Phaser from 'phaser';

/**
 * HiDPI rendering.
 *
 * Every coordinate in ICECAT is authored against a fixed 1280x720 world. The
 * canvas is then stretched to fill the window, so on a retina screen a 1x
 * backing store is upscaled by the browser and every ink keyline softens —
 * which reads as a blurry, low-budget game however good the art is.
 *
 * Phaser 4 has no game-level `resolution` option, and `scale.zoom` only
 * changes the canvas CSS size, so the backing store can only be grown by
 * enlarging the game size itself. The world is kept at 1280x720 by zooming
 * every scene camera by the same factor, which means no layout, arena, or
 * drawing code has to know this happened.
 */
export const WORLD_WIDTH = 1280;
export const WORLD_HEIGHT = 720;

/**
 * Device pixels per world pixel. Capped at 2: past that the fill rate costs
 * more than the sharpness is worth, and rounded so the backing store is always
 * a whole multiple of the world.
 */
export const RENDER_SCALE = Math.min(
  2,
  Math.max(1, Math.round(typeof globalThis.devicePixelRatio === 'number' ? globalThis.devicePixelRatio : 1)),
);

export const CANVAS_WIDTH = WORLD_WIDTH * RENDER_SCALE;
export const CANVAS_HEIGHT = WORLD_HEIGHT * RENDER_SCALE;

export interface CameraWorldViewport {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly centerX: number;
  readonly centerY: number;
}

/**
 * Returns the world-space rectangle currently framed by a Phaser camera.
 *
 * Phaser stores `scrollX` / `scrollY` relative to the camera's backing-store
 * dimensions, not the left/top edge visible after zoom. Those values happen
 * to look like viewport coordinates at 1x, then become wrong on a Retina
 * canvas. Deriving the rectangle with the same maths Phaser uses in
 * `Camera.preRender` keeps room selection and HUD measurements independent of
 * device pixel ratio, and is also current while a camera tween is running.
 */
export function getCameraWorldViewport(camera: Phaser.Cameras.Scene2D.Camera): CameraWorldViewport {
  const width = camera.width / camera.zoomX;
  const height = camera.height / camera.zoomY;
  const centerX = camera.scrollX + camera.width / 2;
  const centerY = camera.scrollY + camera.height / 2;
  return {
    x: centerX - width / 2,
    y: centerY - height / 2,
    width,
    height,
    centerX,
    centerY,
  };
}

/**
 * Frames a scene camera on the 1280x720 world whatever the backing resolution
 * is. `zoom` multiplies on top for the debug inspector.
 */
export function applyRenderScale(camera: Phaser.Cameras.Scene2D.Camera, zoom = 1): void {
  camera.setZoom(RENDER_SCALE * zoom);
  camera.centerOn(WORLD_WIDTH / 2, WORLD_HEIGHT / 2);
}
